import { useId, useState } from 'react';

import type { FieldDefinition, FieldId } from '@inside-us-aos/domain';
import type { SectionProgress } from '@inside-us-aos/form-engine';

import styles from './SectionValidationSummary.module.css';

/**
 * Phase 2 batch 3 Slice 4 — section-level validation surfacing.
 *
 * Renders a summary banner at the top of each section listing the
 * count of incomplete required fields, plus a "Mark section complete"
 * button at the bottom that's gated until every visible+required
 * field validates clean. The submit button's disabled state carries
 * a tooltip listing the blocking field labels for keyboard / hover
 * users — this is the WCAG-friendly equivalent of the spec's
 * "hover/focus shows which fields block".
 *
 * The "complete" state lives in component state (App.tsx). Phase 5
 * persists section-completion alongside the answers so the user can
 * resume mid-application without losing the manual gate.
 */

export interface SectionValidationSummaryProps {
  /** Section progress derived by the form engine. */
  progress: SectionProgress | null;
  /** Catalog field-id → label map for human-readable summaries. */
  fieldsById: ReadonlyMap<FieldId, FieldDefinition>;
  /** Whether the user has marked this section complete. */
  isComplete: boolean;
  /** Callback fired when the user clicks "Mark section complete". */
  onMarkComplete: () => void;
}

export function SectionValidationSummary({
  progress,
  fieldsById,
  isComplete,
  onMarkComplete,
}: SectionValidationSummaryProps): JSX.Element {
  const missingCount = progress?.missingFieldIds.length ?? 0;
  const requiredCount = progress?.required ?? 0;
  const canSubmit = missingCount === 0 && requiredCount > 0;

  const missingLabels = (progress?.missingFieldIds ?? [])
    .map(id => fieldsById.get(id)?.label ?? id)
    .slice(0, 5);

  // Single-line summary with opt-in
  // collapsible details for the missing-questions case. Layout is
  // stable across state flips: the alert always reads as one line
  // (count + toggle), and the bullet list only renders when the user
  // chooses to expand it. No more page jolt when toggling a Yes/No
  // that adds new conditional fields, because the alert above the
  // user's focus stays the same height.
  const detailsListId = useId();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const showInline = missingCount === 1 && missingLabels.length === 1;
  const showToggle = missingCount > 1 && missingLabels.length > 0;

  return (
    <div className={styles.summary}>
      {/*
        Wave 6 Slice 25 (2026-05-02) — single stable alert div instead
        of four conditionally-rendered variant divs.

        Pre-slice each variant rendered a different `<div>` with its
        own `key`, so React unmounted the old alert and mounted the
        new one when the state flipped. The reveal_on_mount fade-in
        fired correctly, but the surrounding page LAYOUT JOLTED
        because the new alert appeared at its full final height
        instantly (the browser had no intermediate state to interpolate
        from).

        Now: one stable `<div>` whose className + content change in
        place. React reuses the same DOM node across state flips —
        the browser can animate `height: auto` between intermediate
        states via the `interpolate-size: allow-keywords` declaration
        in tokens.css. Combined with the `transition: height` rule on
        `.summary__alert`, the alert smoothly resizes when the
        missing-count flips from 0 → N (or vice versa).

        The `reveal_on_mount` class still applies — but only on the
        initial render of the SectionValidationSummary, not on every
        variant flip. That's the intent: page-load gets a fade-in;
        in-flight state changes get a smooth height animation.
      */}
      {(() => {
        const variantKey =
          missingCount > 0
            ? 'missing'
            : isComplete
            ? 'complete'
            : requiredCount > 0
            ? 'ready'
            : 'vacuous';
        const alertClassName =
          missingCount > 0
            ? styles.summary__alert
            : isComplete
            ? styles['summary__alert--complete']
            : requiredCount > 0
            ? styles['summary__alert--ready']
            : styles['summary__alert--vacuous'];
        return (
          <div
            className={`${alertClassName} reveal_on_mount`}
            role="status"
            data-alert-variant={variantKey}
          >
            {missingCount > 0 ? (
              <>
                <div className={styles.summary__alert_row}>
                  <span className={styles.summary__alert_text}>
                    <strong>{missingCount}</strong> question
                    {missingCount === 1 ? '' : 's'}{' '}
                    still need{missingCount === 1 ? 's' : ''} an answer
                    {showInline ? (
                      <>
                        :{' '}
                        <span className={styles.summary__inline_field}>
                          {missingLabels[0]}
                        </span>
                      </>
                    ) : (
                      '.'
                    )}
                  </span>
                  {showToggle ? (
                    <button
                      type="button"
                      className={styles.summary__details_toggle}
                      onClick={() => setDetailsOpen(open => !open)}
                      aria-expanded={detailsOpen}
                      aria-controls={detailsListId}
                    >
                      {detailsOpen ? 'Hide' : 'Show details'}
                      <span
                        aria-hidden="true"
                        className={
                          detailsOpen
                            ? `${styles.summary__details_chevron} ${styles['summary__details_chevron--open']}`
                            : styles.summary__details_chevron
                        }
                      >
                        ▾
                      </span>
                    </button>
                  ) : null}
                </div>
                {showToggle && detailsOpen ? (
                  <ul
                    id={detailsListId}
                    className={styles.summary__list}
                  >
                    {missingLabels.map(label => (
                      <li key={label}>{label}</li>
                    ))}
                    {missingCount > missingLabels.length ? (
                      <li>
                        …and {missingCount - missingLabels.length} more
                      </li>
                    ) : null}
                  </ul>
                ) : null}
              </>
            ) : isComplete ? (
              <>
                {/* Wave 4 Slice 17 — section completion celebration. */}
                <span className={styles.summary__check} aria-hidden="true" />
                <span>
                  <strong>Section complete.</strong> Nicely done — feel
                  free to keep editing or move on.
                </span>
              </>
            ) : requiredCount > 0 ? (
              <>Looks complete — ready to mark this section done?</>
            ) : (
              <>No required questions in this section.</>
            )}
          </div>
        );
      })()}

      {/*
        Phase 7b — the button is no longer disabled when blockers
        exist. Clicking it always invokes the parent's handler, which
        flushes "touched" for every missing required field so the
        primitives surface their errors at once. When the section is
        already complete the button stays disabled so the user can't
        re-mark a clean section.
      */}
      <button
        type="button"
        className={
          canSubmit && !isComplete
            ? `${styles.summary__submit} ${styles['summary__submit--ready']}`
            : styles.summary__submit
        }
        disabled={isComplete}
        title={
          missingCount > 0
            ? `Submit shows what's blocked: ${missingLabels.join(', ')}${
                missingCount > missingLabels.length ? ', …' : ''
              }`
            : isComplete
              ? 'Section already marked complete'
              : 'Mark this section complete'
        }
        onClick={onMarkComplete}
      >
        {/* Wave 2 Slice 7 (2026-05-02) — kept the "Mark section
         * complete" verb form; "Mark complete" alone was too terse for
         * the action's weight. The done-state shortens to a confident
         * "Section complete ✓". */}
        {isComplete ? 'Section complete ✓' : 'Mark section complete'}
      </button>
    </div>
  );
}
